/** Tiny DOM helpers (no framework). */

export type Child = Node | string | null | undefined | false;

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string | null,
  attrs?: Record<string, string | number | boolean | null | undefined> | null,
  children?: Child[],
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (cls) el.className = cls;
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v == null || v === false) continue;
      el.setAttribute(k, v === true ? '' : String(v));
    }
  }
  if (children) append(el, children);
  return el;
}

export function append(el: Element, children: Child[]): void {
  for (const c of children) {
    if (c == null || c === false) continue;
    el.append(typeof c === 'string' ? document.createTextNode(c) : c);
  }
}

/** Parse a static SVG/HTML snippet (trusted, from our own source) into an element. */
export function fromHtml<T extends Element = Element>(html: string): T {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild as T;
}

/** Button with type=button (never submits). */
export function button(
  cls: string,
  content: Child[] | string,
  onClick: (ev: MouseEvent) => void,
  attrs?: Record<string, string | number | boolean | null | undefined>,
): HTMLButtonElement {
  const b = h('button', cls, { type: 'button', ...attrs }, typeof content === 'string' ? [content] : content);
  b.addEventListener('click', onClick);
  return b;
}

/** Writes text only when it changed (avoids needless DOM mutations in the frame loop). */
export class TextSlot {
  private last: string | null = null;
  constructor(readonly el: HTMLElement) {}
  set(text: string): void {
    if (text === this.last) return;
    this.last = text;
    this.el.textContent = text;
  }
}

/** Sets a style transform only when the (rounded) value changed. */
export class TransformSlot {
  private last = Number.NaN;
  constructor(
    readonly el: HTMLElement | SVGElement,
    private readonly fmt: (v: number) => string,
    private readonly epsilon = 0.05,
  ) {}
  set(v: number): void {
    if (Math.abs(v - this.last) < this.epsilon) return;
    this.last = v;
    this.el.style.transform = this.fmt(v);
  }
  invalidate(): void {
    this.last = Number.NaN;
  }
}

/** Toggle a class only when needed. */
export function setClass(el: Element, cls: string, on: boolean): void {
  if (el.classList.contains(cls) !== on) el.classList.toggle(cls, on);
}

export function setHidden(el: HTMLElement, hidden: boolean): void {
  if (el.hidden !== hidden) el.hidden = hidden;
}

/** Stop taps on overlay elements from becoming WebXR select events. */
export function guardXrSelect(el: EventTarget): void {
  el.addEventListener('beforexrselect', (e) => e.preventDefault());
}
